import { useState } from 'react';
import { Card, Stack, Group, Text, Button, Anchor } from '@repletoryreach/sdk-ui';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';

/**
 * The code and the link, and the two ways a freelancer actually shares them:
 * copied into a message, or opened straight in WhatsApp. The link is the short
 * `/r/CODE` form the marketing site redirects, because it survives being read
 * out over the phone.
 */
export function ShareCard({ code, link }: { code: string; link: string }) {
  const t = useT();
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  async function copy(value: string, which: 'code' | 'link') {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      notifySuccess(t('refer.share.copied'));
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // A browser that refuses the clipboard leaves the value on screen to
      // select by hand, which is why it is shown and not hidden behind a button.
    }
  }

  const whatsapp = `https://wa.me/?text=${encodeURIComponent(t('refer.share.message', { link }))}`;

  return (
    <Card withBorder>
      <Stack gap="md">
        <Stack gap="xs">
          <Text role="label">{t('refer.share.title')}</Text>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('refer.share.description')}</Text>
        </Stack>

        <div className="rr-refer-code">
          <span className="rr-refer-code__value">{code}</span>
          <Button variant="light" size="sm" onClick={() => copy(code, 'code')}>
            {copied === 'code' ? t('refer.share.copied') : t('refer.share.copyCode')}
          </Button>
        </div>

        <Stack gap="xs">
          <Text role="caption" c="var(--rr-color-text-muted)">{t('refer.share.linkLabel')}</Text>
          <Text className="rr-refer-link">{link}</Text>
          <Group gap="sm">
            <Button variant="light" size="sm" onClick={() => copy(link, 'link')}>
              {copied === 'link' ? t('refer.share.copied') : t('refer.share.copyLink')}
            </Button>
            <Anchor href={whatsapp} target="_blank" rel="noreferrer">
              <Button variant="subtle" size="sm">{t('refer.share.whatsapp')}</Button>
            </Anchor>
          </Group>
        </Stack>
      </Stack>
    </Card>
  );
}
